import classNames from "classnames";
import {
  ChangeEvent,
  RefObject,
  useContext,
  useEffect,
  useState,
  useRef,
  MutableRefObject,
} from "react";
import { EventOutside } from "../../Dropdown/DropdownBody";
import { defaultPageSettings, TreeContext } from "./CustomDropdown";
import {
  getDropdownBodyHeight,
  getDropdownCoords,
  getDropdownWidth,
} from "../../../../core/helpers/dropdownHelper";
import { Coords } from "../../../../core/interfaces/Coords";
import { LiItem } from "./LiItem";
import TextInput from "../../Inputs/TextInput";
import Icon from "../../Icon";
import LoadingSpinner from "../../Loading/LoadingSpinner";

import styles from "./CustomTreeDropdown.module.css";

export const CustomTree = ({
  onClose,
  parentRef,
  searchValueRef,
  pageSettingsRef,
  itemsToShow,
}: {
  onClose: () => void;
  parentRef: RefObject<any>;
  searchValueRef: MutableRefObject<string>;
  pageSettingsRef: MutableRefObject<{
    page: number;
    pageSize: number;
  }>;
  itemsToShow: number;
}) => {
  const bodyWidth: number | string = getDropdownWidth(parentRef.current);
  const { items, fetchOrganizations, isLoading, canFetchMore } =
    useContext(TreeContext);
  const bodyHeight = getDropdownBodyHeight(
    itemsToShow,
    window.innerHeight <= 750 ? 6 : itemsToShow,
    true
  );
  const coords: Coords = getDropdownCoords(parentRef.current, bodyHeight);
  const [localSearchValue, setLocalSearchValue] = useState(
    searchValueRef.current
  );
  const [needsRefetch, setNeedsRefetch] = useState(false);
  const resultWrapperRef = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const canFetchMoreRef = useRef(canFetchMore);
  const isLoadingRef = useRef(false);

  useEffect(() => {
    canFetchMoreRef.current = canFetchMore;
  }, [canFetchMore]);

  useEffect(() => {
    if (needsRefetch) {
      fetchOrganizations();
      setNeedsRefetch(false);
    }
  }, [needsRefetch]);

  const handleScroll = () => {
    if (resultWrapperRef.current) {
      const { scrollTop, scrollHeight, clientHeight } =
        resultWrapperRef.current;
      if (Math.ceil(scrollTop + clientHeight) >= scrollHeight) {
        if (canFetchMoreRef.current && !isLoadingRef.current) {
          isLoadingRef.current = true;
          fetchOrganizations().finally(() => (isLoadingRef.current = false));
        }
      }
    }
  };

  useEffect(() => {
    const resultWrapper = resultWrapperRef.current;
    if (resultWrapper) {
      resultWrapper.addEventListener("scroll", handleScroll);
    }
    return () => {
      if (resultWrapper) {
        resultWrapper.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  const debouncedSetSearchValue = (value: string) => {
    searchValueRef.current = value;
    pageSettingsRef.current = { ...defaultPageSettings };
    setNeedsRefetch(true);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalSearchValue(value);
    if (intervalRef.current) {
      clearTimeout(intervalRef.current);
      intervalRef.current = null;
    }
    intervalRef.current = setTimeout(() => {
      debouncedSetSearchValue(value);
    }, 500);
  };

  return (
    <EventOutside
      onEventOutside={() => onClose()}
      parentElem={parentRef.current}
    >
      <div
        className={styles.portalWrapper}
        style={{
          top: coords.top,
          left: coords.left,
          width: bodyWidth,
          height: isLoading ? bodyHeight + 64 : bodyHeight,
        }}
        data-cy="organisation-filter-popup"
      >
        <TextInput
          autoFocus
          dataCy="organisation-filter-search"
          className={styles.treeSearch}
          renderIcon={() => <Icon name="search" size={20} />}
          value={localSearchValue}
          onChange={handleInputChange}
        />
        <div
          style={{
            height:
              isLoading && items.length === 0
                ? bodyHeight + 20
                : bodyHeight - 44,
          }}
          className={classNames({
            [styles.resultWrapper]: true,
          })}
          ref={resultWrapperRef}
        >
          {items.length === 0 && isLoading && <LoadingSpinner />}
          {items.map((i) => (
            <LiItem
              key={i.key}
              item={i}
              searchInflicted={!!searchValueRef.current.length}
            />
          ))}
        </div>
        {items.length !== 0 && isLoading && <LoadingSpinner />}
      </div>
    </EventOutside>
  );
};
